import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactSchema } from "../schemas/contactSchema";
import { useVerveContacts } from "./useVerveContacts";
import { useEffect, useMemo, useCallback, useRef } from "react";

export const useVerveContactForm = (initialData = null, onSuccess = null) => {
  const { createContact, updateContact, isMutating } = useVerveContacts();

  const defaultValues = useMemo(() => ({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    district: "",
    state: "",
    pincode: "",
    officeLocation: "",
    assignedTo: "",
    referenceType: "",
    status: "new",
    salesType: null,
    requirementType: "",
    propertyType: "",
    budget: 0,
    customId: "",
    notes: "",
    meetingTime: "",
    alternativeMeetingTime: ""
  }), []);

  const form = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: initialData || defaultValues
  });

  const { reset } = form;

  // Sync form with initialData ONLY when the ID changes to prevent wiping user input during background re-renders
  const lastIdRef = useRef(initialData?._id);

  useEffect(() => {
    const currentId = initialData?._id;
    if (currentId !== lastIdRef.current) {
      console.log("DEBUG - Form Reset triggered due to ID change:", { oldId: lastIdRef.current, newId: currentId });

      // Merge initialData with defaultValues and convert nulls to empty strings
      const sanitizedData = initialData
        ? { ...defaultValues, ...initialData }
        : { ...defaultValues };

      // Ensure no nulls for form fields (especially selects) and extract IDs from objects
      Object.keys(sanitizedData).forEach(key => {
        const val = sanitizedData[key];

        // 1. Convert null/undefined to empty string for consistent form handling
        if (val === null || val === undefined) {
          sanitizedData[key] = "";
          return;
        }

        // 2. Extract _id if the field is a populated object (crucial for assignedTo/telecaller)
        if (typeof val === 'object' && val !== null && val._id) {
          console.log(`DEBUG - Extracting ID for field "${key}":`, val._id);
          sanitizedData[key] = val._id;
        }
      });
      /* Developer : Nithin
Date : 24-04-2026
changes:   Date & time                    */

      // Split dates into date and time fields if present
      const extractDateTime = (dateStr) => {
        if (!dateStr) return { date: "", time: "" };
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return { date: "", time: "" };
        const localIso = new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString();
        return {
          date: localIso.split('T')[0],
          time: localIso.split('T')[1].substring(0, 5)
        };
      };

      if (sanitizedData.preferredDate) {
        const { date, time } = extractDateTime(sanitizedData.preferredDate);
        sanitizedData.preferredDate = date;
        sanitizedData.meetingTime = time === "00:00" ? "" : time;
      }

      if (sanitizedData.alternativeMeetingDate) {
        const { date, time } = extractDateTime(sanitizedData.alternativeMeetingDate);
        sanitizedData.alternativeMeetingDate = date;
        sanitizedData.alternativeMeetingTime = time === "00:00" ? "" : time;
      }

      // Developer: Nithin | Date: 09-07-2026 | Changes: Extract appointmentDate date+time from DB on form load
      if (sanitizedData.appointmentDate) {
        const { date, time } = extractDateTime(sanitizedData.appointmentDate);
        sanitizedData.appointmentDate = date;
        sanitizedData.appointmentTime = time === "00:00" ? "" : time;
      }

      console.log("DEBUG - Final Sanitized Data for Form Reset:", sanitizedData);
      reset(sanitizedData);
      lastIdRef.current = currentId;
    }
  }, [initialData, reset, defaultValues]);

  const onSubmit = async (data) => {
    try {
      // 1. Clone data to avoid mutating form state
      const sanitizedData = { ...data };

      // 2. Remove internal/read-only database fields that shouldn't be sent to API
      const internalFields = [
        '_id', '__v', 'createdAt', 'updatedAt',
        'activityLog', 'teleStatusHistory', 'leadStatusHistory',
        'isConvertedByTelecaller', 'convertedByTelecaller',
        'createdBy', 'updatedBy', 'importId'
      ];
      internalFields.forEach(field => delete sanitizedData[field]);

      // Strip notes if it hasn't changed from initialData
      if (initialData && (sanitizedData.notes === initialData.notes || (!sanitizedData.notes && !initialData.notes))) {
        delete sanitizedData.notes;
      }

      // 3. Sanitize ObjectId fields (Mongoose fails on empty strings for ObjectIds)
      const objectIdFields = ['assignedTo', 'telecaller', 'salesPerson', 'assignedBy'];
      objectIdFields.forEach(field => {
        if (sanitizedData[field] === "") {
          delete sanitizedData[field];
        }
      });

      // 4. Combine date and time fields
      if (sanitizedData.preferredDate && sanitizedData.meetingTime) {
        let datePart = sanitizedData.preferredDate.includes('T') ? sanitizedData.preferredDate.split('T')[0] : sanitizedData.preferredDate;
        sanitizedData.preferredDate = `${datePart}T${sanitizedData.meetingTime}`;
      }
      if (sanitizedData.alternativeMeetingDate && sanitizedData.alternativeMeetingTime) {
        let datePart = sanitizedData.alternativeMeetingDate.includes('T') ? sanitizedData.alternativeMeetingDate.split('T')[0] : sanitizedData.alternativeMeetingDate;
        sanitizedData.alternativeMeetingDate = `${datePart}T${sanitizedData.alternativeMeetingTime}`;
      }
      // Developer: Nithin | Date: 09-07-2026 | Changes: Combine appointmentDate + appointmentTime into ISO string for DB storage
      if (sanitizedData.appointmentDate) {
        let datePart = sanitizedData.appointmentDate.includes('T') ? sanitizedData.appointmentDate.split('T')[0] : sanitizedData.appointmentDate;
        const timeStr = sanitizedData.appointmentTime || "00:00";
        sanitizedData.appointmentDate = `${datePart}T${timeStr}:00`;
      } else {
        delete sanitizedData.appointmentDate;
      }
      delete sanitizedData.meetingTime;
      delete sanitizedData.alternativeMeetingTime;
      delete sanitizedData.appointmentTime;

      console.log("DEBUG - Submitting Contact Payload:", sanitizedData);

      let result;
      if (initialData?._id) {
        console.log("DEBUG - Calling updateContact for ID:", initialData._id);
        result = await updateContact({ id: initialData._id, data: sanitizedData });
        console.log("DEBUG - updateContact Response Received:", result);
      } else {
        console.log("DEBUG - Calling createContact");
        result = await createContact(sanitizedData);
        console.log("DEBUG - createContact Response Received:", result);
      }

      console.log("DEBUG - Submission sequence finished successfully");
      if (onSuccess) onSuccess(result);
    } catch (error) {
      console.error("DEBUG - CRITICAL: Form submission error caught in hook:", error);
      if (error.response) {
        console.error("DEBUG - API Error Response:", error.response.data);
      }
    }
  };

  const resetForm = useCallback(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  // Automatically update status to 'assigned' if a telecaller is selected
  const watchedAssignedTo = useWatch({ control: form.control, name: "assignedTo" });
  const watchedStatus = useWatch({ control: form.control, name: "status" });
  const isAssignedToDirty = form.formState.dirtyFields.assignedTo;

  useEffect(() => {
    // Only auto-trigger if the field was actually touched/changed by the user
    // or if we are creating a brand new contact (no initialData)
    const shouldTrigger = isAssignedToDirty || !initialData?._id;

    if (shouldTrigger) {
      if (watchedAssignedTo && watchedStatus === "new") {
        form.setValue("status", "assigned", { shouldDirty: true });
        form.setValue("teleStatus", "assigned", { shouldDirty: true });
      } else if (!watchedAssignedTo && watchedStatus === "assigned") {
        form.setValue("status", "new", { shouldDirty: true });
        form.setValue("teleStatus", "new", { shouldDirty: true });
      }
    }
  }, [watchedAssignedTo, watchedStatus, form, isAssignedToDirty, initialData?._id]);

  // Log validation errors for debugging
  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0) {
      console.log("DEBUG - Form Validation Errors:", form.formState.errors);
      console.log("DEBUG - Normalization Result for ID:", initialData?._id, form.getValues());
    }
  }, [form.formState.errors, initialData?._id, form]);

  return {
    form,
    control: form.control,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting: isMutating || form.formState.isSubmitting,
    errors: form.formState.errors,
    resetForm
  };
};
